import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { menuAccessGuard } from './core/guards/menu-access.guard';

const routes: Routes = [
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/auth/auth.module').then((m) => m.AuthModule),
      },
    ],
  },
  {
    path: 'main',
    component: MainLayoutComponent,
    children: [
      {
        path: 'payment-report',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'payments' },
        loadChildren: () =>
          import('./features/payment-report/payment-report.module').then(
            (m) => m.PaymentReportModule
          ),
      },
      {
        path: 'wallet-report',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'wallet_report' },
        loadComponent: () =>
          import('./features/wallet-report/wallet-report.component').then(
            (m) => m.WalletReportComponent
          ),
      },
      {
        path: 'membership-report',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'membership_report' },
        loadComponent: () =>
          import('./features/membership-report/membership-report.component').then(
            (m) => m.MembershipReportComponent
          ),
      },
      {
        path: 'cases',
        loadChildren: () =>
          import('./features/case-management/case-management.routes').then(
            (m) => m.CASE_MANAGEMENT_ROUTES
          ),
      },
      {
        path: 'ops-cases',
        loadComponent: () =>
          import('./features/case-management/ops-cases/ops-cases.component').then(
            (m) => m.OpsCasesComponent
          ),
      },
      {
        path: 'ops-cases/:caseId',
        loadComponent: () =>
          import('./features/case-management/ops-case-detail/ops-case-detail.component').then(
            (m) => m.OpsCaseDetailComponent
          ),
      },
      {
        path: 'notification-management',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'notifications' },
        loadChildren: () =>
          import('./features/notification-management/notification-management.routes').then(
            (m) => m.notificationManagementRoutes
          ),
      },
      {
        path: 'staff-attendance',
        loadChildren: () =>
          import('./features/staff-attendance/staff-attendance.routes').then(
            (m) => m.staffAttendanceRoutes
          ),
      },
      {
        path: 'role-permissions',
        loadComponent: () =>
          import('./features/role-permissions/role-permissions.component').then(
            (m) => m.RolePermissionsComponent
          ),
      },
      {
        path: 'membership-record',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'membership_record' },
        loadComponent: () =>
          import('./features/membership-record/membership-record.component').then(
            (m) => m.MembershipRecordComponent
          ),
      },
      {
        path: 'dashboard',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'dashboard' },
        loadChildren: () =>
          import('./features/dashboard/dashboard.module').then(
            (m) => m.DashboardModule
          ),
      },
      {
        path: 'competitions',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'competitions' },
        loadChildren: () =>
          import('./features/competitions/competitions.module').then(
            (m) => m.CompetitionsModule
          ),
      },
      {
        path: 'federation',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'federation' },
        loadChildren: () =>
          import('./features/federation/federation.routes').then(
            (m) => m.federationRoutes
          ),
      },
      {
        path: 'report',
        loadChildren: () =>
          import('./features/report/report.module').then((m) => m.ReportModule),
      },
      {
        path: 'email',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'email' },
        loadChildren: () =>
          import('./features/email/email.module').then((m) => m.EmailModule),
      },
      {
        path: 'ap-plus',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'ap_plus' },
        loadChildren: () =>
          import('./features/ap-plus/ap-plus.module').then((m) => m.ApPlusModule),
      },
      {
        path: 'fund-raiser',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'fund_raiser' },
        loadChildren: () =>
          import('./features/fund-raiser/fund-raiser.module').then(
            (m) => m.FundRaiserModule
          ),
      },
      {
        path: 'court-booking',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'court_booking' },
        loadComponent: () =>
          import('./features/court-booking/court-booking/court-booking.component').then(
            (m) => m.CourtBookingComponent
          ),
      },
      {
        path: 'member',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'member_management' },
        loadChildren: () =>
          import('./features/member/member.module').then((m) => m.MemberModule),
      },
      {
        path: 'stripe',
        loadChildren: () =>
          import('./features/stripe/stripe.module').then((m) => m.StripeModule),
      },
      {
        path: 'setup',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'setup' },
        loadChildren: () =>
          import('./features/setup/setup.module').then((m) => m.SetupModule),
      },
      {
        path: 'polls',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'polls' },
        loadChildren: () =>
          import('./features/polls/polls.module').then((m) => m.PollsModule),
      },
      {
        path: 'shop',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'shop' },
        loadChildren: () =>
          import('./features/shop/shop.module').then((m) => m.ShopModule),
      },
      {
        path: 'fnb',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'fnb' },
        loadChildren: () =>
          import('./features/fnb/fnb.module').then((m) => m.FnbModule),
      },
      {
        path: 'lock-credit-management',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'lock_credits' },
        loadChildren: () =>
          import(
            './features/lock_credit_management/lock_credit_management.module'
          ).then((m) => m.LockCreditManagementModule),
      },
      {
        path: 'refund',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'refund' },
        loadChildren: () =>
          import('./features/refund/refund.module').then(
            (m) => m.RefundModule
          ),
      },
      {
        path: 'payment-link',
        canActivate: [menuAccessGuard],
        data: { menuKey: 'payment_link' },
        loadChildren: () =>
          import('./features/payment-link/payment-link.module').then(
            (m) => m.PaymentLinkModule
          ),
      },
    ],
  },
  { path: '', redirectTo: '/auth/login', pathMatch: 'full' },
  {
    path: 'unsubscribe',
    loadComponent: () =>
      import('./features/unsubscribe/unsubscribe.component').then((m) => m.UnsubscribeComponent),
  },
  { path: '**', redirectTo: '/auth/login' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
